import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { message } from 'antd';
import API_CONFIG from '../config';

interface User {
  id: number;
  email: string;
  role: string;
  company_name?: string;
  licensed_domains: string[];
  licensed_therapeutic_areas: string[];
  effective_therapeutic_areas: string[];
  password_reset_required?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  changePassword: (currentPassword: string | null, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('auth_user');
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        
        // Ensure arrays are properly formatted (defensive check)
        if (user) {
          user.licensed_domains = Array.isArray(user.licensed_domains) 
            ? user.licensed_domains 
            : (user.licensed_domains ? [user.licensed_domains] : []);
          
          user.licensed_therapeutic_areas = Array.isArray(user.licensed_therapeutic_areas) 
            ? user.licensed_therapeutic_areas 
            : (user.licensed_therapeutic_areas ? [user.licensed_therapeutic_areas] : []);
          
          user.effective_therapeutic_areas = Array.isArray(user.effective_therapeutic_areas) 
            ? user.effective_therapeutic_areas 
            : (user.effective_therapeutic_areas ? [user.effective_therapeutic_areas] : []);
        }
        
        setAuthState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    } else {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    // Retry logic with exponential backoff
    const maxRetries = 3;
    let lastError: any = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Create form data for OAuth2 flow
        const formData = new FormData();
        formData.append('username', email);
        formData.append('password', password);

        console.log(`Login attempt ${attempt + 1}/${maxRetries} to:`, `${API_CONFIG.BASE_URL}/auth/token`);
        
        // Increased timeout for production cold starts (30 seconds)
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        
        const response = await fetch(`${API_CONFIG.BASE_URL}/auth/token`, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        console.log('Login response status:', response.status);

        if (!response.ok) {
          let errorMessage = 'Login failed';
          
          try {
            const errorData = await response.json();
            errorMessage = errorData.detail || 'Invalid email or password';
          } catch (e) {
            if (response.status === 429) {
              errorMessage = 'Too many failed login attempts. Your account is temporarily locked. Please try again in 15 minutes.';
            } else {
              errorMessage = `Login failed (${response.status})`;
            }
          }
          
          // Don't retry on authentication errors (401, 403) or rate limit (429)
          if (response.status === 401 || response.status === 403 || response.status === 429) {
            console.error('Login error (non-retryable):', errorMessage);
            return { success: false, error: errorMessage };
          }
          
          // Retry on server errors (5xx) or timeout (408)
          if (response.status >= 500 || response.status === 408) {
            lastError = new Error(errorMessage);
            if (attempt < maxRetries - 1) {
              const delay = 1000 * Math.pow(2, attempt); // Exponential backoff
              console.log(`Retrying login in ${delay}ms...`);
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
          }
          
          console.error('Login error:', errorMessage);
          return { success: false, error: errorMessage };
        }

        const data = await response.json();
        const { access_token } = data;
        console.log('Login successful, got token');

        // Get user profile with the token (with retry logic)
        let userResponse;
        let userData;
        
        for (let profileAttempt = 0; profileAttempt < maxRetries; profileAttempt++) {
          try {
            userResponse = await fetch(`${API_CONFIG.BASE_URL}/auth/me`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${access_token}`,
              },
            });

            if (!userResponse.ok) {
              if (userResponse.status >= 500 && profileAttempt < maxRetries - 1) {
                const delay = 1000 * Math.pow(2, profileAttempt);
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
              }
              message.error('Failed to get user profile');
              return { success: false, error: 'Failed to get user profile' };
            }

            userData = await userResponse.json();
            break; // Success, exit retry loop
          } catch (profileError: any) {
            if (profileAttempt === maxRetries - 1) {
              message.error('Failed to get user profile');
              return { success: false, error: 'Failed to get user profile' };
            }
            const delay = 1000 * Math.pow(2, profileAttempt);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }

        // Ensure arrays are properly formatted (defensive check)
        if (userData) {
          userData.licensed_domains = Array.isArray(userData.licensed_domains) 
            ? userData.licensed_domains 
            : (userData.licensed_domains ? [userData.licensed_domains] : []);
          
          userData.licensed_therapeutic_areas = Array.isArray(userData.licensed_therapeutic_areas) 
            ? userData.licensed_therapeutic_areas 
            : (userData.licensed_therapeutic_areas ? [userData.licensed_therapeutic_areas] : []);
          
          userData.effective_therapeutic_areas = Array.isArray(userData.effective_therapeutic_areas) 
            ? userData.effective_therapeutic_areas 
            : (userData.effective_therapeutic_areas ? [userData.effective_therapeutic_areas] : []);
        }

        // Store auth data
        localStorage.setItem('auth_token', access_token);
        localStorage.setItem('auth_user', JSON.stringify(userData));

        setAuthState({
          user: userData,
          token: access_token,
          isAuthenticated: true,
          isLoading: false,
        });

        message.success(`Welcome back, ${userData.email}!`);
        return { success: true };

      } catch (error: any) {
        console.error(`Login attempt ${attempt + 1} error:`, error);
        
        // Don't retry on abort errors (timeout) or network errors if it's the last attempt
        if (attempt === maxRetries - 1) {
          let errorMessage = 'Login failed. Please try again.';
          if (error.name === 'AbortError') {
            errorMessage = 'Request timed out. The server may be starting up. Please try again in a moment.';
          } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
            errorMessage = 'Network error. Please check your connection.';
          }
          
          return { success: false, error: errorMessage };
        }
        
        // Retry with exponential backoff
        const delay = 1000 * Math.pow(2, attempt);
        console.log(`Retrying login in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    return { success: false, error: 'Login failed after multiple attempts. Please try again.' };
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    // Clear tab persistence on logout
    sessionStorage.removeItem('portal_active_tab');
    sessionStorage.removeItem('admin_active_tab');
    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    message.info('Logged out successfully');
  };

  const refreshToken = async (): Promise<boolean> => {
    const currentToken = localStorage.getItem('auth_token');
    if (!currentToken) return false;
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return false;
      const data = await response.json();
      const newToken = data.access_token;
      localStorage.setItem('auth_token', newToken);
      setAuthState(prev => ({ ...prev, token: newToken }));
      return true;
    } catch {
      return false;
    }
  };

  const changePassword = async (currentPassword: string | null, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    if (!authState.token) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      // Build request body - only include current_password if provided
      const requestBody: any = {
        new_password: newPassword,
      };
      if (currentPassword) {
        requestBody.current_password = currentPassword;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authState.token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        return { success: false, error: data.detail || 'Failed to change password' };
      }

      setAuthState(prev => {
        const updatedUser = prev.user ? { ...prev.user, password_reset_required: false } : prev.user;
        if (updatedUser) {
          localStorage.setItem('auth_user', JSON.stringify(updatedUser));
        }
        return {
          ...prev,
          user: updatedUser,
        };
      });

      message.success('Password updated successfully');
      return { success: true };
    } catch (error: any) {
      console.error('Change password error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshToken,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
